import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import {useInView} from 'framer-motion'
import type {AnimatedListSliceDefault} from 'prismicio-types'
import type {FC} from 'react'
import {useEffect, useRef, useState} from 'react'
import {Container} from 'src/common/Container'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'
import {maskStyles} from './constants'

/**
 * Props for `AnimatedList`.
 */
export type AnimatedListDefaultProps = {
  slice: AnimatedListSliceDefault & {
    slice_type: string
    variation: string
  }
}

/**
 * Component for "AnimatedList" Slices.
 */
export const AnimatedListDefault: FC<AnimatedListDefaultProps> = ({slice}) => {
  const sanitizedTitle = sanitizeHtml(slice.primary.title!)
  const containerRef = useRef(null)
  const [activeStyle, setActiveStyle] = useState(0)
  const listItems = slice.primary.elements.length
  const isInView = useInView(containerRef, {amount: 0.5, once: false})
  const animationDuration = slice.primary.animation_duration || 1500

  // Start animation when the component is in view
  useEffect(() => {
    if (!isInView) {
      return
    }

    const interval = setInterval(() => {
      setActiveStyle((prev) => {
        if (prev + 1 < listItems) {
          return prev + 1
        } else {
          return 0
        }
      })
    }, animationDuration)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- not need to add listItems dependency
  }, [setActiveStyle, isInView])

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative h-[573px] overflow-visible !py-0 lg:h-[800px]"
      containerRef={containerRef}
    >
      <div className="sticky top-0 isolate size-full">
        <picture>
          {isFilled.imageThumbnail(slice.primary.background_image.Mobile) && (
            <source
              media="(max-width: 550px)"
              srcSet={slice.primary.background_image.Mobile.url + '?auto=compress,format&fit=max&w=768'}
              sizes="50vw"
            />
          )}
          <PrismicNextImage
            quality={100}
            field={slice.primary.background_image}
            className="absolute left-0 top-0 -z-10 size-full object-cover"
          />
        </picture>
        <div className="mx-auto flex size-full max-w-[1200px] flex-col justify-between px-5 py-10 md:px-10 lg:px-5 lg:py-16 lg:pb-18 xl:px-0">
          <h2
            className="lg:text-tg-h2 text-[24px] font-normal leading-[normal] text-moss-white lg:leading-[normal]"
            dangerouslySetInnerHTML={{__html: sanitizedTitle}}
          />
          <div className="flex w-full flex-col">
            <div className="w-fit self-start pb-6 lg:hidden">
              <div className="relative h-12 w-full">
                {slice.primary.elements.map((element, index) => (
                  <div
                    key={index}
                    className={cn(
                      'absolute flex w-max items-center gap-3 rounded-[4px] bg-moss-white/[20%] py-2 pl-2 pr-3 backdrop-blur-[40px] transition-opacity duration-700 md:gap-4 md:rounded-lg md:py-2.5 md:pl-2.5 md:pr-4',
                      {
                        'animate-fadeInUp50': activeStyle === index,
                        'animate-fadeOutUp50': activeStyle !== index,
                      },
                    )}
                  >
                    <img
                      loading="lazy"
                      width="1155"
                      height="660"
                      decoding="async"
                      data-nimg="1"
                      className="size-8 md:h-[42px] md:w-[42px]"
                      style={{color: 'transparent'}}
                      sizes="(max-width: 768px) 100vw, 1200px"
                      src={element.icon.url || ''}
                    />
                    <span className="text-[13px] font-semibold uppercase leading-[14px] tracking-[.78px] text-moss-white md:text-[18px] md:leading-normal md:tracking-[.54px]">
                      {element.side_text}
                    </span>
                    <div className="pointer-events-none absolute inset-0 rounded-md" style={maskStyles} />
                  </div>
                ))}
              </div>
            </div>
            <ul className="flex flex-col gap-[11px]">
              {slice.primary.elements.map((element, index) => (
                <li className="flex w-full items-center gap-3" key={index}>
                  <h3
                    className={cn(
                      'w-full text-[clamp(32px,5vw,64px)] font-normal leading-9 tracking-[-.32px] transition-colors duration-700 lg:w-[70%] lg:max-w-[835px] lg:leading-[64px] lg:tracking-[-.64px]',
                      {
                        'text-moss-white': activeStyle === index,
                        'text-moss-white/[70%]': activeStyle !== index,
                      },
                    )}
                  >
                    {element.text}
                  </h3>
                  <div
                    className={cn(
                      'relative flex items-center gap-[16px] rounded-lg border-moss-white bg-moss-white/[20%] py-2.5 pl-2.5 pr-4 backdrop-blur-[40px] transition-opacity duration-700 max-lg:hidden',
                      {
                        'animate-fadeIn': activeStyle === index,
                        'animate-fadeOut': activeStyle !== index,
                      },
                    )}
                  >
                    <PrismicNextImage field={element.icon} className="h-[42px] w-[42px] rounded-[5.33px]" />
                    <span className="text-[18px] font-semibold uppercase leading-normal tracking-[.54px] text-moss-white">
                      {element.side_text}
                    </span>
                    <div className="pointer-events-none absolute inset-0 rounded-md" style={maskStyles} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Container>
  )
}
